import {isNotNullOrUndefined} from '@wandb/weave/common/util/types';
import {type AlignedData} from 'uplot';

import {startFunctionPerformanceMeasure} from '../../../util/datadog/workspace/functionPerformance';
import {type SeriesEntry} from './getSeriesEntries';

const FUNCTION_PERFORMANCE_SAMPLE_RATE = 0.05;

export function getAlignedData(
  seriesEntries: (SeriesEntry | null)[],
  xLogScale?: boolean
): AlignedData {
  // Sample before taking the start timestamp so other calls avoid clock and
  // RUM-event overhead.
  const endMeasure =
    Math.random() < FUNCTION_PERFORMANCE_SAMPLE_RATE
      ? startFunctionPerformanceMeasure('getAlignedData')
      : undefined;

  // Collect all unique x values from all lines and sort them
  const allXValues = new Set<number>();
  for (const entry of seriesEntries) {
    if (entry == null) {
      continue;
    }
    for (const point of entry.line.data) {
      const xValue = point.x;
      // Log scale cannot handle 0 or negative values - filter them out
      if (xLogScale && xValue <= 0) {
        continue;
      }
      allXValues.add(xValue);
    }
  }
  const sortedX = Array.from(allXValues).sort((a, b) => a - b);

  const xValueCount = sortedX.length;
  // Iterate through the sorted x values once to create a map of x -> index
  // to allow fast lookup of where the y value should be stored in the yArrays
  // for a given x.
  const xValueToIndex = new Map<number, number>();
  for (let i = 0; i < xValueCount; i++) {
    xValueToIndex.set(sortedX[i], i);
  }

  // For each line, create a y-values array aligned to sortedX
  // Use null where the line doesn't have data for that x value
  const yArrays = seriesEntries
    .map(entry => {
      if (entry == null) {
        return null;
      }
      const {type, line} = entry;
      // oxlint-disable-next-line unicorn/no-new-array -- https://oxc.rs/docs/guide/usage/linter/rules/unicorn/no-new-array.html
      const yValues = new Array<number | null>(xValueCount).fill(null);
      for (const point of line.data) {
        const index = xValueToIndex.get(point.x);
        if (index != null) {
          if (type === 'band-max') {
            yValues[index] = point.y0 ?? null;
          } else {
            yValues[index] = point.y;
          }
        }
      }
      return yValues;
    })
    .filter(isNotNullOrUndefined);

  endMeasure?.({
    alignmentPath: 'general',
    sampleRate: FUNCTION_PERFORMANCE_SAMPLE_RATE,
    seriesCount: yArrays.length,
    xLogScale: xLogScale === true,
    xValueCount,
  });
  return [sortedX, ...yArrays];
}
